import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export const ALL_MODULES = [
  { key: "admin", label: "Admin" },
  { key: "catalog", label: "Catálogo" },
  { key: "crm", label: "CRM" },
  { key: "assistencia-tecnica", label: "Assistência Técnica" },
  { key: "armazem", label: "Armazém" },
  { key: "contabilidade", label: "Contabilidade" },
  { key: "burocracia", label: "Burocracia" },
  { key: "procurement", label: "Compras" },
  { key: "ia", label: "IA" },
] as const;

export type ModuleKey = typeof ALL_MODULES[number]["key"];

export function useModulePermissions(userId?: string) {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const targetUserId = userId || user?.id;

  const { data: permissions = [], isLoading } = useQuery({
    queryKey: ["module-permissions", targetUserId],
    queryFn: async () => {
      if (!targetUserId) return [];
      const { data, error } = await supabase
        .from("user_module_permissions")
        .select("module")
        .eq("user_id", targetUserId);
      if (error) throw error;
      return data.map((d) => d.module as ModuleKey);
    },
    enabled: !!targetUserId,
  });

  const hasModuleAccess = (moduleKey: ModuleKey): boolean => {
    // Admins see everything
    if (isAdmin) return true;
    // Everyone else needs explicit permission
    return permissions.includes(moduleKey);
  };

  // While auth (and therefore the role) is still resolving, or while the
  // permissions query has not run yet, callers must keep showing a loading
  // state — otherwise route guards redirect a legitimate user away.
  const resolving = authLoading || (!!targetUserId && isLoading);

  return { permissions, hasModuleAccess, isLoading: resolving };
}

export function useManageModulePermissions() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const toggleModule = useMutation({
    mutationFn: async ({ userId, module, grant }: { userId: string; module: ModuleKey; grant: boolean }) => {
      if (grant) {
        const { error } = await supabase
          .from("user_module_permissions")
          .insert({ user_id: userId, module, granted_by: user?.id });
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("user_module_permissions")
          .delete()
          .eq("user_id", userId)
          .eq("module", module);
        if (error) throw error;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["module-permissions", variables.userId] });
    },
  });

  return { toggleModule };
}
